import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans } from "@lingui/macro";
import dayjs from "dayjs";

import API from "@app/services/api";
import QuestionnaireMatrix from "./QuestionnaireMatrix";
import type { Questionnaire, StoredScore } from "@app/utils/questionnaires";

interface QuestionnaireScoreEditorProps {
  questionnaire: Questionnaire;
  /** Le champ corrigé : c'est SON total que l'enregistrement écrit. */
  fieldKey: string;
  tenantId: string;
  patientId: string | number;
  /** Remonte la série recalculée au brouillon de la modale, qui porte l'unique bouton
   *  d'enregistrement. ``series`` vaut null tant qu'aucune réponse n'a été touchée, et
   *  ``computing`` dit que les totaux affichés sont encore en cours de recalcul. */
  onDraftChange: (draft: {
    series: StoredScore[] | null;
    computing: boolean;
  }) => void;
  /** Réponses déjà corrigées lors d'un geste précédent, par date. Le brouillon part de là. */
  corrected?: Record<string, Record<string, number>>;
}

type ComputedScore = { field_key: string; label: string; computed: number | null };

/**
 * Corriger un score en corrigeant les réponses dont il découle.
 *
 * Le total n'est pas saisissable ici, et c'est la décision structurante de cet écran. Dès lors que
 * les items existent, un total modifiable à côté d'eux permet d'enregistrer un dossier où le
 * chiffre contredit ses propres réponses, sans qu'aucune règle ne dise lequel croire. Le médecin
 * corrige la réponse fausse ; le total suit.
 *
 * Il suit en passant par le serveur : la formule de chaque score n'a qu'une implémentation, et
 * pour le SF-12 elle pèse trente-cinq coefficients qu'on ne maintiendra pas deux fois.
 */
export default function QuestionnaireScoreEditor({
  questionnaire,
  fieldKey,
  tenantId,
  patientId,
  onDraftChange,
  corrected,
}: QuestionnaireScoreEditorProps) {

  // Les réponses en cours d'édition, par index de passation. Vide = rien touché sur cette
  // passation, et la matrice affiche alors celles de la source.
  const [draft, setDraft] = useState<Record<number, Record<string, number>>>({});
  const [computed, setComputed] = useState<Record<number, ComputedScore[]>>({});
  const [computing, setComputing] = useState(false);

  const touched = useMemo(
    () => Object.keys(draft).map((k) => Number(k)),
    [draft],
  );

  const handleAnswerChange = useCallback(
    (passationIndex: number, itemKey: string, value: number | null) => {
      setDraft((current) => {
        const passation = questionnaire.passations[passationIndex];
        const alreadyCorrected = passation.date
          ? corrected?.[String(passation.date).slice(0, 10)]
          : undefined;
        // On repart de la dernière vérité posée sur cette passation, pas du document : une
        // deuxième correction ne doit pas rouvrir la première.
        const base =
          current[passationIndex] ?? { ...(alreadyCorrected ?? passation.answers) };
        const next = { ...base };
        if (value === null || Number.isNaN(value)) delete next[itemKey];
        else next[itemKey] = value;
        return { ...current, [passationIndex]: next };
      });
    },
    [questionnaire, corrected],
  );

  // Recalcul des totaux des passations modifiées, débounce pour ne pas appeler à chaque frappe.
  useEffect(() => {
    if (touched.length === 0) return;
    let cancelled = false;
    setComputing(true);
    const timer = setTimeout(() => {
      Promise.all(
        touched.map((index) =>
          API.post({
            path: `/patient/${tenantId}/${patientId}/questionnaires/compute`,
            body: {
              questionnaire_id: questionnaire.id,
              answers: draft[index],
            },
          })
            .then((res: { data?: { scores?: ComputedScore[] } }) => ({
              index,
              scores: res?.data?.scores ?? [],
            }))
            .catch(() => ({ index, scores: [] as ComputedScore[] })),
        ),
      ).then((results) => {
        if (cancelled) return;
        setComputed((current) => {
          const next = { ...current };
          for (const { index, scores } of results) next[index] = scores;
          return next;
        });
        setComputing(false);
      });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft, touched, questionnaire.id, tenantId, patientId]);

  /**
   * La série que l'enregistrement écrit : une entrée par passation, le total du champ corrigé.
   *
   * Les passations non touchées gardent la valeur calculée à la lecture. Corriger une réponse d'une
   * passation ne doit pas réécrire les treize autres — mais les omettre supprimerait la courbe,
   * puisque la valeur enregistrée REMPLACE la série au lieu de la compléter.
   */
  const nextSeries = useMemo<StoredScore[]>(() => {
    const out: StoredScore[] = [];
    questionnaire.passations.forEach((passation, index) => {
      if (!passation.date) return;
      const day = String(passation.date).slice(0, 10);
      const wasCorrected = corrected?.[day];
      const isTouched = touched.includes(index);

      const edited = computed[index]?.find((s) => s.field_key === fieldKey);
      const original = passation.scores.find((s) => s.field_key === fieldKey);
      const value = isTouched
        ? (edited?.computed ?? null)
        : (original?.computed ?? null);
      if (value === null) return;

      // Les réponses ne sont portées que sur les passations qu'un clinicien a réellement
      // touchées — celle-ci maintenant, ou une précédente. Les poser partout ferait dire au
      // dossier qu'il a arbitré quatorze passations quand il en a ouvert une.
      const answers = isTouched ? draft[index] : wasCorrected;
      out.push(answers ? { date: passation.date, score: value, items: answers } : { date: passation.date, score: value });
    });
    return out;
  }, [questionnaire, computed, touched, fieldKey, draft, corrected]);

  // La série remonte au brouillon de la modale dès qu'elle change. C'est le seul endroit
  // d'où cet éditeur parle : il n'enregistre rien lui-même.
  useEffect(() => {
    onDraftChange({
      series: touched.length > 0 ? nextSeries : null,
      computing,
    });
  }, [nextSeries, touched, computing, onDraftChange]);

  const editedLabel = questionnaire.scores.find((s) => s.field_key === fieldKey)?.label;

  return (
    <>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          <Trans>
            Corrigez les réponses du questionnaire : le total est recalculé et n'est pas saisi
            à la main.
          </Trans>
        </p>

        <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-200">
          <QuestionnaireMatrix
            questionnaire={questionnaire}
            fieldKey={fieldKey}
            onAnswerChange={handleAnswerChange}
            draft={draft}
            corrected={corrected}
          />
        </div>

        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          {touched.length === 0 ? (
            <span className="text-slate-500">
              <Trans>Aucune réponse modifiée pour l'instant.</Trans>
            </span>
          ) : (
            <div className="space-y-1">
              <div className="font-medium text-slate-700">
                {editedLabel} —{" "}
                {computing ? (
                  <span className="font-normal text-slate-500">
                    <Trans>recalcul…</Trans>
                  </span>
                ) : (
                  <span className="font-normal text-slate-600">
                    {nextSeries.length}{" "}
                    <Trans>valeur(s) après correction</Trans>
                  </span>
                )}
              </div>
              {touched.map((index) => {
                const passation = questionnaire.passations[index];
                const after = computed[index]?.find((s) => s.field_key === fieldKey);
                const before = passation.scores.find((s) => s.field_key === fieldKey);
                return (
                  <div key={index} className="text-slate-600 tabular-nums">
                    {dayjs(passation.date).format("DD/MM/YYYY")} :{" "}
                    {before?.computed ?? "·"} →{" "}
                    <span className="font-semibold text-primary">
                      {after?.computed ?? "·"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

    </>
  );
}
